import { format } from 'date-fns';
import { ampli } from '../../ampli';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { useEffect, useState, useMemo } from "react";
import { API, ShareAPI } from "../../API";
import { useTranslation } from "react-i18next";
import { WidgetContentModal } from '../WidgetContentModal/WidgetContentModal';
import { AddToNarrativeModal } from '../AddToNarrativeModal/AddToNarrativeModal';
import { AddToSourceGroupModal } from '../AddToSourceGroupModal/AddToSourceGroupModal';
import { platformMapping } from '../../utils/platforms';
import { VerticalBarChart } from '../VerticalBarChart/VerticalBarChart';
import { showRequestError } from '../../utils/requestError';

const BAR_COLOR = '#3b82f6';



export const PlatformsChart = ({
  report,
  story,
  isShare,
  isClickable=true,
  sourceGroups,
  countries,
  countryGroups = [],
  startDate,
  endDate
}) => {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const [platforms, setPlatforms] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filter, setFilter] = useState(null);
  const [modalTitle, setModalTitle] = useState('');
  const [addToCaseModal, setAddToCaseModal] = useState({ isOpen: false, platforms: null, });
  const [addToGroupModal, setAddToGroupModal] = useState({ isOpen: false, platforms: null, });
  const api = isShare ? ShareAPI : API;

  const bucketNameMap = useMemo(() => ({
    'FACEBOOK': t('Facebook'),
    'TWITTER': t('Twitter'),
    'TELEGRAM': t('Telegram'),
    'YOUTUBE': t('Youtube'),
    'WEB': t('Web'),
    'TIKTOK': t('TikTok'),
    'VK': t('VK'),
    'OK': t('OK'),
    'INSTAGRAM': t('Instagram'),
    'THREADS': t('Threads'),
    'LINKEDIN': t('LinkedIn'),
  }), [t]);

  const getPlatforms = () => {
    if (!story) return;
    const urlParams = new URLSearchParams();
    urlParams.append('breakdown', 'platform');
    if(sourceGroups.length > 0) {
      sourceGroups.forEach((sourceGroup) => {
        urlParams.append('source_group_ids', sourceGroup.value);
      });
    }
    if(countries.length > 0) {
      countries.forEach((country) => {
        urlParams.append('source_country_ids', country.value);
      });
    }
    if (countryGroups.length > 0) {
      countryGroups.forEach((groupId) => {
        urlParams.append('country_group_ids', groupId);
      });
    }
    if(startDate) {
      urlParams.append('date_from',format(startDate, 'yyyy-LL-dd'));
    }
    if(endDate) {
      urlParams.append('date_to',format(endDate, 'yyyy-LL-dd'));
    }

    api.fetch('GET', `/API/v1/topic-modeling/${report?.id}/events/${story?.id}/sources/breakdown?${urlParams.toString()}`).then(
      data => setPlatforms(data)
    ).catch((error) => showRequestError(error, t('Platforms')))
  }

  useEffect(() => {
    getPlatforms()
  }, [story])

  const chartItems = useMemo(
    () =>
      (platforms?.objects ?? []).map((item) => ({
        platform: item.platform,
        name: bucketNameMap[item.platform] ?? item.platform,
        value: item.sources_count,
        color: BAR_COLOR,
      })),
    [platforms, bucketNameMap],
  );

  const onSelect = (index) => {
    const item = index === null ? undefined : chartItems[index];
    if (!item) return;
    setFilter(item.platform);
    setModalTitle(item.name);
    setIsModalOpen(true);
    ampli.track({
      event_type: 'Open wiget popup in event',
      event_properties: {
        user_id: currentUser?.id,
        workspace_id: currentUser?.workspace_id,
        report_id: report?.id,
        popup_type: 'Actor Types: Platforms',
        popup_value: 'Platform: ' + item.name,
      }
    });
  };

  return (
    <>
      {platforms ? (
        <VerticalBarChart
          chartData={chartItems}
          isClickable={isClickable}
          onSelect={onSelect}
          seriesName={t('Actors')}
        />
      ) : (
        <VerticalBarChart chartData={null} />
      )}

      {isModalOpen ? (
        <WidgetContentModal
          infoModal={isModalOpen}
          isChart={true}
          title={modalTitle}
          platformOption={[{ value: filter, label: bucketNameMap[filter] }]}
          type={null}
          subType={null}
          stateAffiliatedId={null}
          countryId={countries?.length > 0 ? countries : null}
          // platformsOptions={platformOptions}
          audienceRange={null}
          narrative={report}
          sourceGroup={
            sourceGroups.length > 0
              ? sourceGroups?.map((sourceGroup) => sourceGroup.value)
              : null
          }
          source={null}
          sentimentOptions={null}
          eventIdsOptions={[story.id]}
          startDate={startDate}
          endDate={endDate}
          contentTypeOptions={null}
          isShare={isShare}
          onChange={(bool) => {
            setIsModalOpen(bool);
          }}
          showAddToActions={true}
          onAddToCase={(bool, sourceId) => {
            setAddToCaseModal({
              isOpen: bool,
              platforms: platformMapping[filter],
              sourceId: sourceId,
            });
          }}
          onAddToGroup={(bool, sourceId) => {
            setAddToGroupModal({
              isOpen: bool,
              platforms: platformMapping[filter],
              sourceId: sourceId,
            });
          }}
        />
      ) : (
        ''
      )}
      {addToCaseModal.isOpen ? (
        <AddToNarrativeModal
          isOpen={addToCaseModal.isOpen}
          deduplication={false}
          caseId={report?.id}
          eventIds={[story?.id]}
          areAllMessagesSelected={true}
          startDate={startDate}
          endDate={endDate}
          sourceGroups={sourceGroups}
          countries={countries}
          sources={addToCaseModal.sourceId ? [addToCaseModal.sourceId] : []}
          platforms={[addToCaseModal.platforms]}
          onChange={(bool) => {
            setAddToCaseModal({
              isOpen: bool,
              contentTypes: null,
              sentiment: null,
              sourceId: null,
            });
          }}
        />
      ) : (
        ''
      )}
      {addToGroupModal.isOpen ? (
        <AddToSourceGroupModal
          isOpen={addToGroupModal.isOpen}
          deduplication={false}
          areAllMessagesSelected={true}
          startDate={startDate}
          endDate={endDate}
          sourceGroups={sourceGroups}
          sourcesFilter={addToGroupModal.sourceId ? [addToGroupModal.sourceId] : []}
          countries={countries}
          platforms={[addToGroupModal.platforms]}
          narrative={report}
          eventIds={[story?.id]}
          onChange={(bool) => {
            setAddToGroupModal({
              isOpen: bool,
              contentTypes: null,
              sentiment: null,
              sourceId: null,
            });
          }}
        />
      ) : (
        ''
      )}
    </>
  );
};